import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Search, X } from 'lucide-react';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Button } from '@vehix/portal/components/ui/button';
import { Checkbox } from '@vehix/portal/components/ui/checkbox';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import { cn } from '@vehix/portal/lib/utils';
import { getOemTheme } from '../../utils/oemColors';
import type { AvailableZone } from '../../services/pipelineOrchestrator';

/**
 * Zone picker shared by the Run Pipeline and Schedule dialogs.
 *
 * Both dialogs previously carried byte-identical flat checkbox lists with no
 * search, no grouping and no bulk selection. With 196 active zones across 11
 * OEMs (Ford alone is 95) a single-OEM run meant ninety-five clicks.
 *
 * Three things make that tractable here: OEM groups that collapse, a search
 * that scopes "select all" to its matches, and a live task count — because a
 * zone expands to one task per collector, so 94 zones quietly became 188 tasks.
 */

// Preferred defaults when a zone offers them, matching the previous behaviour.
const PREFERRED_COLLECTORS = ['autotrader_crawler', 'cargurus_crawler'];

/** Extracted so the JSX line stays under max-len; prettier won't wrap it. */
const SEARCH_ICON_CLASS = 'absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground';

/**
 * A picked zone and the collectors to run for it.
 *
 * Only `zone_slug` and `collectors` reach the wire — both dialogs flatten to
 * `{zone_slug, collector}` pairs on submit. The display fields are optional
 * because a saved schedule stores only slugs, so edit-mode hydration cannot
 * recover them; the list renders names from the live zone data instead.
 */
export interface ZoneSelection {
  zone_slug: string;
  collectors: string[];
  zone_name?: string | undefined;
  oem?: string | undefined;
  state?: string | undefined;
}

export interface CollectorOption {
  collector: string;
  display_name: string;
}

/** Case-insensitive match across the fields an admin would actually type. */
function matches(zone: AvailableZone, term: string): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return (
    zone.zone_name.toLowerCase().includes(t) ||
    zone.zone_slug.toLowerCase().includes(t) ||
    (zone.oem ?? '').toLowerCase().includes(t) ||
    (zone.state ?? '').toLowerCase().includes(t)
  );
}

/** Collectors to use for a zone given the run-level defaults. */
function resolveCollectors(zone: AvailableZone, defaults: string[]): string[] {
  const supported = zone.available_collectors.map(c => c.collector);
  const fromDefaults = defaults.filter(c => supported.includes(c));
  if (fromDefaults.length > 0) return fromDefaults;

  const preferred = supported.filter(c => PREFERRED_COLLECTORS.includes(c));
  if (preferred.length > 0) return preferred;

  const isDefault = zone.available_collectors.filter(c => c.is_default).map(c => c.collector);
  if (isDefault.length > 0) return isDefault;

  return supported.length > 0 ? [supported[0] as string] : ['cargurus_crawler'];
}

type CheckedState = boolean | 'indeterminate';

function checkedFor(selectedCount: number, total: number): CheckedState {
  if (total === 0 || selectedCount === 0) return false;
  return selectedCount === total ? true : 'indeterminate';
}

interface ZoneRowProps {
  zone: AvailableZone;
  selected: ZoneSelection | undefined;
  onToggle: (zone: AvailableZone) => void;
  onToggleCollector: (slug: string, collector: string) => void;
}

/**
 * Checkbox + Label with `htmlFor` rather than a wrapping `<label>`: Radix
 * renders the checkbox as a `<button>`, which is a labelable element, so the
 * association gives a real click target and keeps the a11y lint honest.
 */
const ZoneRow: React.FC<ZoneRowProps> = ({ zone, selected, onToggle, onToggleCollector }) => {
  const id = `zone-${zone.zone_slug}`;
  return (
    <div className={cn('px-3 py-2 hover:bg-muted/50', selected && 'bg-primary/5')}>
      <div className='flex items-center gap-2'>
        <Checkbox id={id} checked={!!selected} onCheckedChange={() => onToggle(zone)} />
        <Label htmlFor={id} className='min-w-0 cursor-pointer font-normal'>
          <span className='block truncate text-sm font-medium'>{zone.zone_name}</span>
          <span className='block text-xs text-muted-foreground'>
            {zone.oem}
            {zone.state ? ` · ${zone.state}` : ''}
          </span>
        </Label>
      </div>

      {selected && zone.available_collectors.length > 1 && (
        <div className='ml-6 mt-1.5 flex flex-wrap gap-x-4 gap-y-1'>
          {zone.available_collectors.map(c => {
            const cid = `${id}-${c.collector}`;
            return (
              <div key={c.collector} className='flex items-center gap-1.5'>
                <Checkbox
                  id={cid}
                  checked={selected.collectors.includes(c.collector)}
                  onCheckedChange={() => onToggleCollector(zone.zone_slug, c.collector)}
                />
                <Label htmlFor={cid} className='cursor-pointer text-xs font-normal'>
                  {c.display_name}
                </Label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface ZoneGroupProps {
  oem: string;
  zones: AvailableZone[];
  selectedCount: number;
  open: boolean;
  onToggleOpen: (oem: string) => void;
  onSelectGroup: (zones: AvailableZone[], selected: boolean) => void;
  selectedBySlug: Map<string, ZoneSelection>;
  onToggleZone: (zone: AvailableZone) => void;
  onToggleCollector: (slug: string, collector: string) => void;
}

// eslint-disable-next-line max-lines-per-function
const ZoneGroup: React.FC<ZoneGroupProps> = ({
  oem,
  zones,
  selectedCount,
  open,
  onToggleOpen,
  onSelectGroup,
  selectedBySlug,
  onToggleZone,
  onToggleCollector
}) => (
  <div>
    {/* OEM header: the checkbox selects the whole group */}
    <div
      className={
        'sticky top-0 z-10 flex items-center gap-2 bg-background/95 px-3 py-1.5 backdrop-blur'
      }
    >
      <Checkbox
        checked={checkedFor(selectedCount, zones.length)}
        onCheckedChange={checked => onSelectGroup(zones, checked !== false)}
        aria-label={`Select all ${oem} zones`}
      />
      <button
        type='button'
        onClick={() => onToggleOpen(oem)}
        className='flex flex-1 items-center gap-2 text-left'
      >
        {open ? (
          <ChevronDown className='h-3.5 w-3.5 text-muted-foreground' />
        ) : (
          <ChevronRight className='h-3.5 w-3.5 text-muted-foreground' />
        )}
        <span className={cn('inline-block h-2 w-2 rounded-full', getOemTheme(oem).dotClassName)} />
        <span className='text-xs font-medium'>{oem}</span>
        <Badge variant='outline' className='text-[10px]'>
          {selectedCount > 0 ? `${selectedCount}/` : ''}
          {zones.length}
        </Badge>
      </button>
    </div>

    {open &&
      zones.map(zone => (
        <ZoneRow
          key={zone.zone_slug}
          zone={zone}
          selected={selectedBySlug.get(zone.zone_slug)}
          onToggle={onToggleZone}
          onToggleCollector={onToggleCollector}
        />
      ))}
  </div>
);

interface PipelineZoneSelectorProps {
  zones: AvailableZone[] | undefined;
  value: ZoneSelection[];
  onChange: (next: ZoneSelection[]) => void;
  isLoading?: boolean | undefined;
  loadError?: unknown;
  /** Shown instead of the list when there is no tenant selected yet. */
  placeholder?: string | undefined;
  invalid?: boolean | undefined;
}

// eslint-disable-next-line max-lines-per-function, complexity
export const PipelineZoneSelector: React.FC<PipelineZoneSelectorProps> = ({
  zones,
  value,
  onChange,
  isLoading,
  loadError,
  placeholder,
  invalid
}) => {
  const [search, setSearch] = useState('');
  /** Explicit closes, used when groups start open (small zone counts). */
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  /** Explicit opens, used when groups start closed (large zone counts). */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [defaultCollectors, setDefaultCollectors] = useState<string[]>(PREFERRED_COLLECTORS);

  const allZones = useMemo(() => (Array.isArray(zones) ? zones : []), [zones]);
  const selectedBySlug = useMemo(() => new Map(value.map(z => [z.zone_slug, z])), [value]);

  /** Every collector any zone offers, for the run-level default toggles. */
  const collectorOptions = useMemo(() => {
    const map = new Map<string, CollectorOption>();
    for (const z of allZones) {
      for (const c of z.available_collectors) {
        if (!map.has(c.collector)) {
          map.set(c.collector, { collector: c.collector, display_name: c.display_name });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [allZones]);

  const visible = useMemo(() => allZones.filter(z => matches(z, search)), [allZones, search]);

  /** OEM -> zones, ordered by OEM then zone name. */
  const groups = useMemo(() => {
    const map = new Map<string, AvailableZone[]>();
    for (const z of visible) {
      const oem = z.oem || 'Unknown';
      const existing = map.get(oem) ?? [];
      existing.push(z);
      map.set(oem, existing);
    }
    return Array.from(map.entries())
      .map(([oem, list]) => ({
        oem,
        zones: [...list].sort((a, b) => a.zone_name.localeCompare(b.zone_name))
      }))
      .sort((a, b) => a.oem.localeCompare(b.oem));
  }, [visible]);

  // A search implies you want to see what matched, so matching groups open
  // regardless of their collapsed state.
  const searching = search.trim().length > 0;

  // OEM groups always start closed, whatever the zone count. Opening them by
  // default buried every brand after the first under its own rows, and the
  // useful first move is almost always "which brand", not "which zone".
  // Searching still force-opens every group (see `groupOpen`).
  const autoCollapse = groups.length > 1;

  const groupOpen = (oem: string): boolean => {
    if (searching) return true;
    return autoCollapse ? expanded.has(oem) : !collapsed.has(oem);
  };

  const toggleGroupOpen = (oem: string) => {
    if (autoCollapse) {
      setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(oem)) next.delete(oem);
        else next.add(oem);
        return next;
      });
    } else {
      setCollapsed(prev => {
        const next = new Set(prev);
        if (next.has(oem)) next.delete(oem);
        else next.add(oem);
        return next;
      });
    }
  };

  const toSelection = useCallback(
    (zone: AvailableZone): ZoneSelection => ({
      zone_slug: zone.zone_slug,
      zone_name: zone.zone_name,
      oem: zone.oem,
      state: zone.state,
      collectors: resolveCollectors(zone, defaultCollectors)
    }),
    [defaultCollectors]
  );

  /** Adds/removes a set of zones, preserving any per-zone collector overrides. */
  const setZonesSelected = useCallback(
    (target: AvailableZone[], selected: boolean) => {
      const slugs = new Set(target.map(z => z.zone_slug));
      if (!selected) {
        onChange(value.filter(z => !slugs.has(z.zone_slug)));
        return;
      }
      const additions = target.filter(z => !selectedBySlug.has(z.zone_slug)).map(toSelection);
      onChange([...value, ...additions]);
    },
    [onChange, value, selectedBySlug, toSelection]
  );

  const toggleZone = (zone: AvailableZone) => {
    setZonesSelected([zone], !selectedBySlug.has(zone.zone_slug));
  };

  const toggleCollector = (slug: string, collector: string) => {
    onChange(
      value.map(z => {
        if (z.zone_slug !== slug) return z;
        const has = z.collectors.includes(collector);
        // A zone with no collector would produce no work at all.
        if (has && z.collectors.length === 1) return z;
        return {
          ...z,
          collectors: has ? z.collectors.filter(c => c !== collector) : [...z.collectors, collector]
        };
      })
    );
  };

  /**
   * Run-level collector defaults. Changing them re-applies across everything
   * already selected — that is the point of a default, and the per-zone
   * checkboxes remain available for exceptions afterwards.
   */
  const toggleDefaultCollector = (collector: string) => {
    const next = defaultCollectors.includes(collector)
      ? defaultCollectors.filter(c => c !== collector)
      : [...defaultCollectors, collector];
    if (next.length === 0) return; // at least one collector must remain
    setDefaultCollectors(next);

    const bySlug = new Map(allZones.map(z => [z.zone_slug, z]));
    onChange(
      value.map(sel => {
        const zone = bySlug.get(sel.zone_slug);
        if (!zone) return sel;
        return { ...sel, collectors: resolveCollectors(zone, next) };
      })
    );
  };

  const visibleSelectedCount = visible.filter(z => selectedBySlug.has(z.zone_slug)).length;
  const taskCount = value.reduce((sum, z) => sum + z.collectors.length, 0);
  const collectorSpread = value.length > 0 ? (taskCount / value.length).toFixed(1) : '0';

  // ------------------------------------------------------------------ states
  if (placeholder) return <p className='text-xs text-muted-foreground'>{placeholder}</p>;

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 py-4'>
        <Loader2 className='h-4 w-4 animate-spin' />
        <span className='text-xs text-muted-foreground'>Loading zones...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <p className='text-xs text-red-500'>
        Failed to load zones. The pipeline service may not be available.
      </p>
    );
  }

  if (allZones.length === 0) {
    return <p className='text-xs text-muted-foreground'>No zones available for this tenant</p>;
  }

  return (
    <div className='space-y-2'>
      {/* Collector defaults — the multiplier behind the task count */}
      {collectorOptions.length > 1 && (
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-xs text-muted-foreground'>Collectors:</span>
          {collectorOptions.map(opt => {
            const on = defaultCollectors.includes(opt.collector);
            return (
              <button
                key={opt.collector}
                type='button'
                onClick={() => toggleDefaultCollector(opt.collector)}
                className={cn(
                  'rounded-md border px-2 py-1 text-xs transition-colors',
                  on ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:bg-muted/50'
                )}
              >
                {opt.display_name}
              </button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className='relative'>
        <Search className={SEARCH_ICON_CLASS} />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Search zones by name, OEM or state...'
          className='h-8 pl-9 text-xs'
        />
        {search && (
          <button
            type='button'
            onClick={() => setSearch('')}
            className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground'
            aria-label='Clear search'
          >
            <X className='h-3.5 w-3.5' />
          </button>
        )}
      </div>

      {/* Select-all over whatever the search currently matches */}
      <div className='flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2'>
        <Checkbox
          checked={checkedFor(visibleSelectedCount, visible.length)}
          onCheckedChange={checked => setZonesSelected(visible, checked !== false)}
          aria-label={searching ? 'Select all matching zones' : 'Select all zones'}
        />
        <span className='text-xs font-medium'>
          {searching ? `Select all ${visible.length} matching` : `Select all ${visible.length}`}
        </span>
        {value.length > 0 && (
          <Button
            variant='ghost'
            size='sm'
            className='ml-auto h-6 text-xs'
            onClick={() => onChange([])}
          >
            Clear {value.length}
          </Button>
        )}
      </div>

      {/* Grouped list */}
      <div
        className={cn(
          'max-h-64 overflow-y-auto rounded-lg border divide-y',
          invalid && 'border-red-500'
        )}
      >
        {groups.length === 0 && (
          <p className='px-3 py-6 text-center text-xs text-muted-foreground'>
            No zones match “{search.trim()}”.
          </p>
        )}

        {groups.map(group => (
          <ZoneGroup
            key={group.oem}
            oem={group.oem}
            zones={group.zones}
            selectedCount={group.zones.filter(z => selectedBySlug.has(z.zone_slug)).length}
            open={groupOpen(group.oem)}
            onToggleOpen={toggleGroupOpen}
            onSelectGroup={setZonesSelected}
            selectedBySlug={selectedBySlug}
            onToggleZone={toggleZone}
            onToggleCollector={toggleCollector}
          />
        ))}
      </div>

      {/* What this actually costs, before the run is submitted */}
      {value.length > 0 && (
        <p
          className={cn(
            'text-xs',
            taskCount > 200 ? 'font-medium text-amber-500' : 'text-muted-foreground'
          )}
        >
          {value.length} {value.length === 1 ? 'zone' : 'zones'} × {collectorSpread} collectors
          {' = '}
          <span className='font-medium'>{taskCount} tasks</span>
          {taskCount > 200 && ' — this is a large run'}
        </p>
      )}
    </div>
  );
};
